import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { flowRight as compose } from 'lodash';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { injectIntl } from 'react-intl';
import Table from 'react-bootstrap/Table';
import FormControl from 'react-bootstrap/FormControl';
import Button from 'react-bootstrap/Button';

import CustomPagination from '../../CustomPagination';
import CurrencyConverter from '../../CurrencyConverter';
import Loader from '../../../components/Common/Loader';

import getUnpaidBookings from './getUnpaidBookings.graphql';
import settleBookingPaymentMutation from './settleBookingPayment.graphql';
import { showToaster } from '../../../helpers/toastrMessgaes/showToaster';
import debounce from '../../../helpers/debounce';

import s from './UnpaidBookingList.css';
import bt from '../../../components/commonStyle.css';

const PAYMENT_LABEL = { 2: 'Card', 3: 'Wallet', 4: 'Google/Apple Pay' };

export class UnpaidBookingList extends Component {
	static propTypes = {
		bookingDetails: PropTypes.object,
	}

	constructor(props) {
		super(props)
		this.state = {
			currentPage: 1,
			settlingId: null,
		}
		this.paginationData = this.paginationData.bind(this);
		this.handleSettle = this.handleSettle.bind(this);
		this.handleKeywordSearch = debounce(this.handleKeywordSearch.bind(this), 250);
	}

	paginationData(currentPage) {
		const { bookingDetails: { refetch } } = this.props;
		this.setState({ currentPage });
		refetch({ currentPage });
	}

	handleKeywordSearch(searchList) {
		const { bookingDetails: { refetch } } = this.props;
		this.setState({ currentPage: 1 });
		refetch({ currentPage: 1, searchList });
	}

	/*
	 * Menandai lunas TIDAK memungut uang apa pun. Ini pencatatan bahwa
	 * pembayaran sudah beres di luar app — transfer bank, tunai ke kantor —
	 * sehingga booking berhenti menggantung dan payout providernya bisa jalan.
	 */
	async handleSettle(bookingId, amount, currency) {
		const reason = window.prompt(
			`Mark booking #${bookingId} (${currency || ''} ${amount || 0}) as paid.\n\n` +
			`This does NOT collect any money. Use it only when payment was already received outside the app.\n\n` +
			`How was it paid?`
		);
		if (reason === null) return;
		if (String(reason).trim().length < 3) {
			showToaster({ messageId: 'errorMessage', requestContent: { content: 'A reason is required.' }, toasterType: 'error' });
			return;
		}

		this.setState({ settlingId: bookingId });
		try {
			const { settleBookingPaymentMutation, bookingDetails: { refetch } } = this.props;
			const { data } = await settleBookingPaymentMutation({ variables: { bookingId, reason } });
			if (data && data.settleBookingPayment && data.settleBookingPayment.status === 200) {
				showToaster({ messageId: 'settleSuccess', toasterType: 'success' });
				await refetch({ currentPage: this.state.currentPage });
			} else {
				showToaster({
					messageId: 'errorMessage',
					requestContent: { content: data && data.settleBookingPayment && data.settleBookingPayment.errorMessage },
					toasterType: 'error'
				});
			}
		} catch (error) {
			showToaster({ messageId: 'errorMessage', requestContent: { content: error.message }, toasterType: 'error' });
		}
		this.setState({ settlingId: null });
	}

	/// Umur tagihan dalam hari. Makin tua, makin kecil peluang tertagih —
	/// itu sebabnya kolom ini ada dan daftarnya diurutkan dari yang terlama.
	ageInDays(createdAt) {
		if (!createdAt) return null;
		const then = new Date(Number(createdAt) || createdAt).getTime();
		if (!then) return null;
		return Math.floor((Date.now() - then) / 86400000);
	}

	render() {
		const { bookingDetails, loading } = this.props;
		const { currentPage, settlingId } = this.state;
		const data = bookingDetails && bookingDetails.getUnpaidBookings;
		const results = (data && data.results) || [];
		const count = (data && data.count) || 0;

		const total = results.reduce((sum, item) => sum + Number(item.userPayableFare || 0), 0);

		return (
			<div className={s.widthInner}>
				<div className={cx(s.searchInput, 'searchInputRTL')}>
					<FormControl
						type='text'
						placeholder='Search'
						onChange={(e) => this.handleKeywordSearch(e.target && e.target.value)}
						className={bt.formControlInput}
					/>
				</div>

				{(loading || (bookingDetails && bookingDetails.loading)) && <div><Loader type="circle" /></div>}

				{!loading && (
					<div>
						<div className={cx(s.summaryBar, 'textAlignRightRTL')}>
							{count} job completed but never paid
							{results.length > 0 && ` — ${results[0].currency || ''} ${total.toFixed(2)} on this page`}
						</div>

						<div className={cx(s.tableCss, 'tableCss', 'tableSticky', 'NewResponsiveTable')}>
							<Table className="table">
								<thead>
									<tr>
										<th scope="col">ID</th>
										<th scope="col">Customer</th>
										<th scope="col">Contact</th>
										<th scope="col">Service</th>
										<th scope="col">Provider</th>
										<th scope="col">Amount</th>
										<th scope="col">Method</th>
										<th scope="col">Age</th>
										<th scope="col">Provider paid?</th>
										<th scope="col">Action</th>
									</tr>
								</thead>
								<tbody>
									{results.length === 0 && (
										<tr><td colSpan={10}>Nothing outstanding.</td></tr>
									)}
									{results.map((item) => {
										const user = item.userDetails || {};
										const userData = user.userData || {};
										const age = this.ageInDays(item.createdAt);
										return (
											<tr key={item.id}>
												<td data-label="ID">{item.id}</td>
												<td data-label="Customer">{user.firstName || '—'}</td>
												<td data-label="Contact">
													{userData.email || '—'}
													{userData.phoneNumber ? <div>{userData.phoneDialCode}{userData.phoneNumber}</div> : null}
												</td>
												<td data-label="Service">{(item.categoryDetails && item.categoryDetails.name) || '—'}</td>
												<td data-label="Provider">{(item.partnerDetails && item.partnerDetails.firstName) || '—'}</td>
												<td data-label="Amount">
													<CurrencyConverter from={item.currency} amount={item.userPayableFare} />
												</td>
												<td data-label="Method">{PAYMENT_LABEL[item.paymentType] || item.paymentType}</td>
												<td data-label="Age">{age === null ? '—' : `${age} d`}</td>
												{/*
												  * Payout mensyaratkan pembayaran pelanggan selesai, jadi
												  * selama booking ini menggantung providernya juga belum
												  * dibayar padahal pekerjaannya sudah selesai.
												  */}
												<td data-label="Provider paid?">{item.isPayoutPaid ? 'Yes' : 'No'}</td>
												<td data-label="Action">
													<Button
														className={cx(bt.btnPrimary, bt.btnSmall)}
														disabled={settlingId === item.id}
														onClick={() => this.handleSettle(parseInt(item.id, 10), item.userPayableFare, item.currency)}
													>
														{settlingId === item.id ? '...' : 'Mark as paid'}
													</Button>
												</td>
											</tr>
										);
									})}
								</tbody>
							</Table>
						</div>

						{count > 0 && (
							<CustomPagination
								total={count}
								currentPage={currentPage}
								defaultCurrent={1}
								defaultPageSize={10}
								change={this.paginationData}
								paginationLabel='bookings'
							/>
						)}
					</div>
				)}
			</div>
		);
	}
}

const mapState = (state) => ({
	loading: state.intl.loading
});

export default compose(
	injectIntl,
	withStyles(s, bt),
	graphql(getUnpaidBookings, {
		name: 'bookingDetails',
		options: {
			variables: { currentPage: 1, searchList: '' },
			ssr: true,
			fetchPolicy: 'network-only'
		}
	}),
	graphql(settleBookingPaymentMutation, { name: 'settleBookingPaymentMutation' }),
	connect(mapState)
)(UnpaidBookingList);
